/**
 * WT-231: a live launch while the tab's saved runtime pointer belongs to a
 * different program.
 *
 * The saved pointer is a single slot. A launch for another program used to
 * throw a dead-end error. Now the earlier program is put on a small "shelf"
 * (a second sessionStorage key, tab-scoped like the pointer) and the requested
 * program starts. Opening the earlier program's link later takes its entry
 * back off the shelf and resumes the same server session through the normal
 * resume path (including WT-230's sign-in recovery for a patient-bound session).
 *
 * Nothing here writes to a server session: the earlier session is only ever
 * read, never changed or replaced.
 */
import {
  getTreatmentSession,
  isTerminalTreatmentSessionError,
} from "./authorityApi";
import { isRuntimePatientPermissionError } from "./patientRuntimeIdentity";
import {
  TREATMENT_RUNTIME_STATE,
  TREATMENT_SOURCE_KIND,
  TREATMENT_STORAGE_KEY,
} from "./treatmentApiConstants";
import { TreatmentRuntimeResumeError } from "./treatmentRuntimeResumeError";

export const EARLIER_INTAKE_IN_PROGRESS = "earlier_intake_in_progress";

const SHELF_KEY = "welliemd_treatment_runtime_shelf_v1";
const SHELF_LIMIT = 5;

// Sessions the patient cannot continue; shelving them would only park a dead end.
const FINISHED_STATES = new Set([
  TREATMENT_RUNTIME_STATE.completed,
  TREATMENT_RUNTIME_STATE.checkoutConsumed,
  TREATMENT_RUNTIME_STATE.expired,
  TREATMENT_RUNTIME_STATE.disqualified,
]);

// Real progress the patient should be asked about before leaving it.
const ASK_FIRST_STATES = new Set([
  TREATMENT_RUNTIME_STATE.productSelection,
  TREATMENT_RUNTIME_STATE.checkoutReady,
]);

/** The pointer field recording an ask-first state; empty for every other state. */
export const askFirstStateHint = (state) => (
  ASK_FIRST_STATES.has(state) ? { state_hint: state } : {}
);

const launchKey = (sourceKind, sourceId, apiBaseUrl) => (
  `${sourceKind}|${sourceId}|${apiBaseUrl}`
);

const pointerKey = (pointer) => launchKey(
  pointer.source_kind,
  String(pointer.source_id),
  pointer.runtime_api_base_url,
);

const readShelf = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SHELF_KEY) || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (_error) {
    return {};
  }
};

const writeShelf = (shelf) => {
  try {
    sessionStorage.setItem(SHELF_KEY, JSON.stringify(shelf));
  } catch (_error) {
    // A full or blocked storage only costs the "come back to it" convenience.
  }
};

export const shelveTreatmentPointer = (pointer) => {
  if (!pointer?.session_token || !pointer.source_kind || !pointer.source_id) return;
  const shelf = readShelf();
  const key = pointerKey(pointer);
  delete shelf[key];
  shelf[key] = pointer;
  const keys = Object.keys(shelf);
  keys.slice(0, Math.max(0, keys.length - SHELF_LIMIT))
    .forEach((oldest) => delete shelf[oldest]);
  writeShelf(shelf);
};

/** Put the tab's active pointer on the shelf and clear it (the "Start this one" choice). */
export const shelveActiveTreatmentPointer = () => {
  let active = null;
  try {
    active = JSON.parse(
      sessionStorage.getItem(TREATMENT_STORAGE_KEY.runtimePointer) || "null",
    );
  } catch (_error) {
    active = null;
  }
  shelveTreatmentPointer(active);
  sessionStorage.removeItem(TREATMENT_STORAGE_KEY.runtimePointer);
};

export const readTreatmentShelf = () => readShelf();

const isGone = (error) => (
  (typeof isTerminalTreatmentSessionError === "function"
    && isTerminalTreatmentSessionError(error))
  || [404, 410].includes(Number(error?.status))
);

const hasPatientToken = () => (
  !!sessionStorage.getItem(TREATMENT_STORAGE_KEY.patientAccessToken)
);

/**
 * What the earlier session is doing. A patient-bound session cannot be read
 * without that patient's token, so the request is skipped and the state the
 * pointer last recorded (only kept for the ask-first states) is used instead.
 */
const readEarlierState = async (saved) => {
  const unreadable = saved.patient_bound === true && !hasPatientToken();
  if (!unreadable) {
    try {
      const earlier = await getTreatmentSession(
        saved.session_token,
        null,
        saved.runtime_api_base_url,
      );
      return { gone: false, state: earlier?.state || null };
    } catch (error) {
      if (isGone(error)) return { gone: true, state: null };
      if (!isRuntimePatientPermissionError(error)) {
        throw new TreatmentRuntimeResumeError(
          "runtime_resume_failed",
          "Saved treatment progress could not be restored. Retry or start over.",
          {
            cause_code: error?.code || error?.rawCode || null,
            status: error?.status || null,
            retryable: error?.retryable ?? true,
          },
        );
      }
    }
  }
  return { gone: false, state: saved.state_hint || null };
};

export class EarlierIntakeChoiceError extends TreatmentRuntimeResumeError {
  constructor(saved, requested, state) {
    super(EARLIER_INTAKE_IN_PROGRESS, "You have an intake in progress.", {
      earlier: {
        sourceKind: saved.source_kind,
        sourceId: String(saved.source_id),
        apiBaseUrl: saved.runtime_api_base_url,
        state,
      },
      requested: {
        sourceKind: requested.sourceKind,
        sourceId: String(requested.sourceId),
        apiBaseUrl: requested.apiBaseUrl,
      },
    });
    this.name = "EarlierIntakeChoiceError";
    // A choice, not a failure: nothing to retry and no "start over".
    this.action = null;
  }
}

const matchesLaunch = (saved, requested) => (
  saved.mode === requested.sourceKind
  && saved.source_kind === requested.sourceKind
  && String(saved.source_id) === String(requested.sourceId)
  && saved.runtime_api_base_url === requested.apiBaseUrl
);

/** Take the requested program's own parked pointer back, unless that session is over. */
const restoreShelved = async (requested) => {
  const key = launchKey(requested.sourceKind, requested.sourceId, requested.apiBaseUrl);
  const shelf = readShelf();
  const entry = shelf[key];
  if (!entry) return null;
  delete shelf[key];
  writeShelf(shelf);
  if (!entry.session_token) return null;
  if (!(entry.patient_bound === true && !hasPatientToken())) {
    try {
      const session = await getTreatmentSession(
        entry.session_token,
        null,
        entry.runtime_api_base_url,
      );
      if (FINISHED_STATES.has(session?.state)) return null;
    } catch (error) {
      if (isGone(error)) return null;
      // Anything else is reported by the normal resume that follows.
    }
  }
  sessionStorage.setItem(TREATMENT_STORAGE_KEY.runtimePointer, JSON.stringify(entry));
  return entry;
};

/**
 * Decide which saved pointer, if any, a launch continues with.
 *
 * - pointer matches the launch: it is used (WT-230's resume and sign-in path)
 * - pointer belongs to another program: the patient is asked first if it is at
 *   product selection or checkout; otherwise it is shelved and the requested
 *   program starts, or resumes from its own shelved pointer
 *
 * Returns the pointer to resume, or null to start a new session.
 */
export const settleLaunchPointer = async (saved, requested) => {
  if (saved && matchesLaunch(saved, requested)) return saved;
  if (saved) {
    let shelve = true;
    // Another tenant API is another clinic: its intake cannot be continued here.
    if (saved.runtime_api_base_url === requested.apiBaseUrl) {
      const earlier = await readEarlierState(saved);
      if (earlier.gone || FINISHED_STATES.has(earlier.state)) shelve = false;
      else if (ASK_FIRST_STATES.has(earlier.state)) {
        throw new EarlierIntakeChoiceError(saved, requested, earlier.state);
      }
    }
    if (shelve) shelveTreatmentPointer(saved);
    sessionStorage.removeItem(TREATMENT_STORAGE_KEY.runtimePointer);
  }
  return restoreShelved(requested);
};

/** The current page address, pointed at the earlier program (the "Continue" choice). */
export const earlierIntakeUrl = (href, earlier) => {
  const url = new URL(href);
  url.searchParams.delete("program_id");
  url.searchParams.delete("custom_program_id");
  url.searchParams.delete("runtime_session_token");
  url.searchParams.set(
    earlier.sourceKind === TREATMENT_SOURCE_KIND.customProgram
      ? "custom_program_id"
      : "program_id",
    earlier.sourceId,
  );
  return url.toString();
};
